// Profil-Nachbau F-07, F-08 (output/profil-team-20261014/BAU-COCKPIT.md 1.1 „Formate“): Zahlen, Prozent, Datum und Dauer
// nach de-AT. Beträge formatiert diese Datei nie; Euro kommt nur im Plattform-Cockpit und nur über netMoney
// (administration/billing/shared.tsx, GB-04).
// Zahlen mit Tausenderpunkt wie in Österreich üblich („1.234“, wie billing/inhaberWerte.ts tokenZahl): Intl setzt für de-AT
// ein geschütztes Leerzeichen als Tausendertrennung, deshalb ersetzt zahl() nur dieses Zeichen.
import { DIAGRAMM } from './cockpitTexte';

const ZAHL = new Intl.NumberFormat('de-AT');
const ZAHL_EINE_STELLE = new Intl.NumberFormat('de-AT', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const MONAT = new Intl.DateTimeFormat('de-AT', { month: 'long', year: 'numeric', timeZone: 'UTC' });
const MONAT_KURZ = new Intl.DateTimeFormat('de-AT', { month: 'long', timeZone: 'UTC' });
const mitPunkt = (format: Intl.NumberFormat, wert: number) => format.formatToParts(wert).map(teil => teil.type === 'group' ? '.' : teil.value).join('');

export const zahl = (wert: number | null) => wert === null ? '–' : mitPunkt(ZAHL, wert);
/** Eine Nachkommastelle, auch bei ganzen Zahlen: „4,0“, „4,3“. */
export const zahlEineStelle = (wert: number) => mitPunkt(ZAHL_EINE_STELLE, wert);
/** „9 %“, „16,7 %“, „unter 1 %“; „–“ ohne Wert. */
export function prozent(stand: { prozent: number; unterEinem?: boolean } | number | null | undefined): string {
  if (stand === null || stand === undefined) return '–';
  if (typeof stand === 'number') return `${zahl(stand)} %`;
  if (stand.unterEinem) return 'unter 1 %';
  return `${zahl(stand.prozent)} %`;
}
/** JJJJ-MM-TT oder ISO-Zeitpunkt → TT.MM.JJJJ (Datum aus dem Text, ohne Umweg über die Ortszeit). */
export const datum = (wert: string | null | undefined) => wert ? `${wert.slice(8, 10)}.${wert.slice(5, 7)}.${wert.slice(0, 4)}` : '';
/** ISO-Zeitpunkt → TT.MM.JJJJ in Wien. */
export const datumWien = (iso: string | null | undefined) => iso
  ? new Intl.DateTimeFormat('de-AT', { timeZone: 'Europe/Vienna', day: '2-digit', month: '2-digit', year: 'numeric' }).format(new Date(iso)) : '';
/** TT.MM. eines Tags (Achse). */
export const tagKurz = (tag: string) => `${tag.slice(8, 10)}.${tag.slice(5, 7)}.`;
/** „Oktober 2026“ bzw. „Oktober“ (mit „Jänner“). */
export const monatLang = (monat: string) => MONAT.format(new Date(Date.UTC(Number(monat.slice(0, 4)), Number(monat.slice(5, 7)) - 1, 1)));
export const monatName = (monat: string) => MONAT_KURZ.format(new Date(Date.UTC(Number(monat.slice(0, 4)), Number(monat.slice(5, 7)) - 1, 1)));
/** Ein Abschnitt des Verlaufs: Tag „01.10.2026“, Woche „28.09. bis 04.10.2026“, Monat „Oktober 2026“. */
export function abschnittText(einheit: 'TAG' | 'WOCHE' | 'MONAT', von: string, bis: string): string {
  if (einheit === 'WOCHE') return `${tagKurz(von)} bis ${datum(bis)}`;
  return einheit === 'MONAT' ? monatLang(von) : datum(von);
}
/** Profil-Nachbau F-25 (SPEC 2.1): 4 bis 6 runde Teilstriche (1, 2 oder 5 × 10ⁿ, ganze Zahlen) von 0 bis über max;
 *  wie Entwurf A mittel/bausteine.js schritte. 0 bis 7 → 0, 2, 4, 6, 8; 25 → 0, 5 … 25; 0 → 0, 1. */
export function teilstriche(max: number): number[] {
  const kandidaten: number[] = [];
  for (let e = 0; e < 9; e++) for (const f of [1, 2, 5]) kandidaten.push(f * 10 ** e);
  const schritt = kandidaten.find(s => Math.ceil(Math.max(1, max) / s) <= 5) ?? kandidaten[kandidaten.length - 1];
  const n = Math.max(1, Math.ceil(Math.max(1, max) / schritt));
  return Array.from({ length: n + 1 }, (_, i) => i * schritt);
}
/** Wochentag eines Tags, 0 = Montag. */
export const wochentag = (tag: string) => (new Date(Date.UTC(Number(tag.slice(0, 4)), Number(tag.slice(5, 7)) - 1, Number(tag.slice(8, 10)))).getUTCDay() + 6) % 7;
/** Achse: Tag und Woche „13.10.“, Monat „Okt.“, im Jänner mit Jahr („Jän. 2027“), wenn die Reihe zwei Jahre berührt. */
export function achseText(einheit: 'TAG' | 'WOCHE' | 'MONAT', von: string, mitJahr = false): string {
  if (einheit !== 'MONAT') return tagKurz(von);
  const monat = Number(von.slice(5, 7)) - 1;
  return `${DIAGRAMM.monateKurz[monat]}${monat === 0 && mitJahr ? ` ${von.slice(0, 4)}` : ''}`;
}
/** Kopf des Tooltips: Tag „Mo., 13.10.2026“, Woche „05.10. bis 11.10.2026“, Monat „Oktober 2026“; der laufende Tag mit
 *  „ (heute, bis jetzt)“, eine Woche oder ein Monat bis heute mit „ (bis heute)“. */
export function tippKopf(einheit: 'TAG' | 'WOCHE' | 'MONAT', von: string, bis: string, heute: string): string {
  if (einheit === 'TAG') return `${DIAGRAMM.wochentageKurz[wochentag(von)]}, ${datum(von)}${von === heute ? DIAGRAMM.heute : ''}`;
  return `${abschnittText(einheit, von, bis)}${bis === heute ? DIAGRAMM.bisHeute : ''}`;
}
/** „1 Std. 45 Min.“, unter einer Stunde „45 Min.“; Stunden mit Tausenderpunkt. */
export function dauer(minuten: number): string {
  const ganz = Math.max(0, Math.round(minuten)), std = Math.floor(ganz / 60), min = ganz % 60;
  return std === 0 ? `${min} Min.` : `${zahl(std)} Std. ${min} Min.`;
}
export type Trend = { art: 'PROZENT'; prozent: number } | { art: 'NEU' } | { art: 'KEIN' };
/** „+12 %“, „−5 %“, „±0 %“, „neu“, „–“. */
export function trendText(trend: Trend): string {
  if (trend.art === 'NEU') return 'neu';
  if (trend.art === 'KEIN') return '–';
  return trend.prozent > 0 ? `+${zahl(trend.prozent)} %` : trend.prozent < 0 ? `−${zahl(-trend.prozent)} %` : '±0 %';
}
/** Eingabe einer ganzen Zahl ohne Zeichen und ohne Trennung („15“); null bei allem anderen, auch bei leer. */
export function ganzeZahl(text: string): number | null {
  const roh = text.trim();
  return /^\d{1,6}$/.test(roh) ? Number(roh) : null;
}
